import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/api';

interface FileItem {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size?: number;
  modified?: string;
}

interface FilePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (filePath: string) => void;
  allowedExtensions?: string[]; // Например: ['.png', '.jpg', '.webp', '.svg']
}

const FilePickerModal: React.FC<FilePickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  allowedExtensions = ['.png', '.jpg', '.jpeg', '.webp', '.svg', '.gif']
}) => {
  const [currentPath, setCurrentPath] = useState<string>('/');
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    } else {
      // Сбрасываем путь при закрытии
      setCurrentPath('/');
    }
  }, [isOpen, currentPath]);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet<{ files: FileItem[] }>(`/files/list?path=${encodeURIComponent(currentPath)}`);
      const allFiles = data.files || [];
      
      // Фильтруем файлы по расширениям (только изображения)
      const filteredFiles = allFiles.filter(file => {
        if (file.type === 'directory') return true;
        const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        return allowedExtensions.includes(ext);
      });
      
      setFiles(filteredFiles);
    } catch (err: any) {
      // Handle 404 specifically for initial folder creation
      if (err.message && err.message.includes('ENOENT')) {
        setFiles([]); // Folder doesn't exist yet, show empty
      } else {
        setError(err.message || 'Ошибка загрузки файлов');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path: string) => {
    if (path === '..') {
      const parentPath = currentPath.split('/').slice(0, -1).join('/') || '/';
      setCurrentPath(parentPath);
    } else {
      const newPath = currentPath === '/' ? `/${path}` : `${currentPath}/${path}`;
      setCurrentPath(newPath);
    }
  };

  const handleFileSelect = (file: FileItem) => {
    if (file.type === 'directory') {
      handleNavigate(file.name);
    } else {
      // Возвращаем путь к файлу
      onSelect(file.path);
      onClose();
    }
  };

  const getBreadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean);
    return ['/', ...parts];
  };

  const navigateToPath = (path: string) => {
    setCurrentPath(path);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col m-4">
        {/* Заголовок */}
        <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Выберите файл</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>

        {/* Хлебные крошки */}
        <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            {getBreadcrumbs().map((part, index) => {
              const path = part === '/' ? '/' : '/' + getBreadcrumbs().slice(1, index + 1).join('/');
              const isLast = index === getBreadcrumbs().length - 1;
              return (
                <React.Fragment key={index}>
                  <button
                    onClick={() => !isLast && navigateToPath(path)}
                    className={`text-xs ${isLast ? 'text-gray-800 font-semibold' : 'text-blue-600 hover:text-blue-800'}`}
                    disabled={isLast}
                  >
                    {part === '/' ? 'Корень' : part}
                  </button>
                  {!isLast && <span className="text-gray-400 text-xs">/</span>}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Содержимое */}
        <div className="flex-1 overflow-y-auto px-4 py-3 min-h-0">
          {loading ? (
            <div className="text-center py-4 text-gray-500">Загрузка...</div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">{error}</div>
          ) : files.length === 0 ? (
            <div className="text-center py-4 text-gray-500">Папка пуста</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {/* Кнопка "Назад" если не в корне */}
              {currentPath !== '/' && (
                <button
                  onClick={() => handleNavigate('..')}
                  className="flex flex-col items-center justify-center p-2 border-2 border-dashed border-gray-300 rounded hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-2xl mb-1">📁</div>
                  <div className="text-xs text-gray-700 text-center">..</div>
                  <div className="text-xs text-gray-500">Назад</div>
                </button>
              )}

              {/* Файлы и папки */}
              {files.map((file) => (
                <button
                  key={file.path}
                  onClick={() => handleFileSelect(file)}
                  className="flex flex-col items-center justify-center p-2 border-2 border-gray-200 rounded hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-2xl mb-1">
                    {file.type === 'directory' ? '📁' : '🖼️'}
                  </div>
                  <div className="text-xs text-gray-700 text-center break-words w-full line-clamp-2">
                    {file.name}
                  </div>
                  {file.type === 'file' && file.size && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Подвал */}
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilePickerModal;

